import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import {
  AdvisedFertilizerResponse,
  DeleteFertilizersByManureCode,
  DeleteFertilizersBySourceRequest,
  FertilizerRequest,
  MineralBalanceResponse,
  PostFertilizersResponse,
} from 'src/app/fertilizers/models/fertilizer.model';
import { Environment } from 'src/environments/environment';

import { ManureSpace } from '../models/manure-space.models';

@Injectable({
  providedIn: 'root',
})
export class ManureSpaceApiService {
  protected readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected baseUrl = this.env.USE_LEGACY_MANURE_SPACE_ENDPOINT
    ? `${this.env.API_BASE_URL}/service/manure-space/secure/`
    : `${this.env.APIM_BASE_URL}/arable-service/`;

  postFertilizers(manureSpaceId: number, fertilizers: FertilizerRequest[]) {
    return this.http.post<PostFertilizersResponse>(
      `${this.baseUrl}manurespace/${manureSpaceId}/fertilizers`,
      { fertilizers },
    );
  }

  deleteFertilizer(manureSpaceId: number, fertilizerId: string) {
    return this.http.delete(
      `${this.baseUrl}manurespace/${manureSpaceId}/fertilizers/${fertilizerId}`,
    );
  }

  private filterParams<T extends object>(params: T): Partial<T> {
    return Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== null && v !== undefined),
    ) as Partial<T>;
  }

  deleteFertilizersByManureCode(manureSpaceId: number, req: DeleteFertilizersByManureCode) {
    const params = new HttpParams({ fromObject: this.filterParams(req) });
    return this.http.delete<{ manureSpaceId: number }>(
      `${this.baseUrl}manurespace/${manureSpaceId}/fertilizers`,
      { params },
    );
  }

  deleteFertilizersByPeriod(
    manureSpaceId: number,
    periodId: number,
    req: DeleteFertilizersByManureCode,
  ) {
    const params = new HttpParams({ fromObject: this.filterParams(req) });
    return this.http.delete<{ manureSpaceId: number }>(
      `${this.baseUrl}manurespace/${manureSpaceId}/period/${periodId}/fertilizers`,
      { params },
    );
  }

  getManureSpace(cropYear: number) {
    return this.http.get<ManureSpace>(`${this.baseUrl}manurespace/cropyear/${cropYear}`);
  }

  getManureSpaceByManureCode(cropYear: number) {
    return this.http.get<ManureSpace>(
      `${this.baseUrl}manurespace/summed-by-manure-code/cropyear/${cropYear}`,
    );
  }

  getManureSpaceByPeriod(cropYear: number) {
    return this.http.get<ManureSpace>(
      `${this.baseUrl}manurespace/summary/period/cropyear/${cropYear}`,
    );
  }

  deleteFertilizersBySource(req: DeleteFertilizersBySourceRequest) {
    return this.http.post<{ manureSpaceId: number }>(
      `${this.baseUrl}manurespace/deleteFertilizersBySource`,
      req,
    );
  }

  postComment(manureSpaceId: number, comment = '') {
    return this.http.post(`${this.baseUrl}manurespace/${manureSpaceId}/comment`, { comment });
  }

  postInitialStockMatchesRvo(manureSpaceId: number, initialStockMatchesRvo: boolean) {
    return this.http.post(`${this.baseUrl}manurespace/${manureSpaceId}/initialStockMatchesRvo`, {
      initialStockMatchesRvo,
    });
  }

  getMineralBalance(deliveriesFrom: string, deliveriesUntil: string) {
    return this.http.get<MineralBalanceResponse>(`${this.baseUrl}manurespace/MineralBalance`, {
      params: {
        deliveriesFrom,
        deliveriesUntil,
      },
    });
  }

  getAdvisedFertilizers(cropYear: number) {
    return this.http.get<AdvisedFertilizerResponse>(
      `${this.baseUrl}manurespace/AdvisedFertilizers`,
      {
        params: {
          cropYear,
        },
      },
    );
  }
}
